<template>
  <!--
    KPI (count) appearance controls, split out of WizardStepBehavior to keep that step under the
    project line ceiling. Same contract: config is the parent's single source of truth, passed by
    reference and mutated in place (v-model + inline sets); the only side-effecting handler, the
    schema dropdown change, is emitted up (schema-change) so the lookup stays in EditSqlViewWidget.
    Rendered only for a non-gauge count widget (the parent gates it with v-if).
  -->
  <!-- eslint-disable vue/no-mutating-props -->
  <div class="wizard-step-behavior-kpi">
    <span class="viz-sub-label mt-3">Icon</span>
    <div class="kpi-icon-picker">
      <v-btn
        small
        outlined
        class="kpi-icon-picker__opt kpi-icon-picker__none"
        :class="{ 'kpi-icon-picker__opt--active': !config.icon }"
        :color="!config.icon ? 'primary' : undefined"
        @click="config.icon = ''">
        None
      </v-btn>
      <v-btn
        v-for="ic in kpiIconOptions"
        :key="ic"
        icon
        small
        class="kpi-icon-picker__opt"
        :class="{ 'kpi-icon-picker__opt--active': config.icon === ic }"
        :color="config.icon === ic ? 'primary' : undefined"
        @click="config.icon = ic">
        <v-icon small>{{ ic }}</v-icon>
      </v-btn>
    </div>
    <span class="viz-sub-hint">Appears beside the value as a status mark.</span>

    <!-- Status: dynamic (Auto, thresholds over the value, recomputed on every fetch)
         or a fixed colour (the legacy manual tone, an escape hatch for decorative
         counts). Mirrors the Font-size v-btn-toggle pattern above. -->
    <span class="viz-sub-label mt-3">Status</span>
    <v-btn-toggle v-model="config.statusMode" mandatory dense>
      <v-btn small :value="statusModes.AUTO"><v-icon x-small class="mr-1">mdi-auto-fix</v-icon> Auto from value</v-btn>
      <v-btn small :value="statusModes.FIXED"><v-icon x-small class="mr-1">mdi-palette</v-icon> Fixed color</v-btn>
      <v-btn small :value="statusModes.SCHEMA"><v-icon x-small class="mr-1">mdi-palette-swatch</v-icon> By schema</v-btn>
    </v-btn-toggle>

    <!-- Auto: absolute-band thresholds. The user sets only direction; the serializer
         derives the op per rule (higherIsWorse -> gte, lowerIsWorse -> lte). -->
    <template v-if="config.statusMode === statusModes.AUTO">
      <span v-if="!kpiHasThreshold" class="viz-sub-hint mt-3">
        No status set yet. The number shows in plain ink until you add a caution or critical value.
      </span>

      <span class="viz-sub-label mt-3">Direction</span>
      <v-btn-toggle v-model="config.direction" mandatory dense>
        <v-btn small :value="kpiDirections.HIGHER_IS_WORSE"><v-icon x-small class="mr-1">mdi-arrow-up</v-icon> Higher is worse</v-btn>
        <v-btn small :value="kpiDirections.LOWER_IS_WORSE"><v-icon x-small class="mr-1">mdi-arrow-down</v-icon> Lower is worse</v-btn>
      </v-btn-toggle>
      <span class="viz-sub-hint">Choose which way this number turns bad.</span>

      <span class="viz-sub-label mt-3">Caution from</span>
      <v-text-field
        v-model.number="config.cautionValue"
        type="number"
        placeholder="Not set"
        outlined
        dense
        hide-details
        class="kpi-threshold-field">
      </v-text-field>
      <span class="viz-sub-hint">{{ kpiCautionHint }}</span>

      <span class="viz-sub-label mt-3">Critical from</span>
      <v-text-field
        v-model.number="config.criticalValue"
        type="number"
        placeholder="Not set"
        outlined
        dense
        hide-details
        class="kpi-threshold-field">
      </v-text-field>
      <span class="viz-sub-hint">{{ kpiCriticalHint }}</span>

      <p v-if="kpiThresholdError" class="viz-threshold-error warning--text mt-2" role="alert">
        <v-icon x-small color="warning" class="mr-1">mdi-alert-circle-outline</v-icon>
        {{ kpiThresholdError }}
      </p>
    </template>

    <!-- Fixed: legacy manual tone = a fixed colour. Zero-migration escape hatch; the
         evaluator falls back to it only when no thresholds are set. -->
    <template v-else-if="config.statusMode === statusModes.FIXED">
      <span class="viz-sub-hint mt-3">
        Use a fixed color only for tiles that are not measuring severity, like a decorative count.
      </span>
      <div class="kpi-tone-picker">
        <v-btn
          small
          outlined
          class="kpi-tone-picker__opt"
          :class="{ 'kpi-tone-picker__opt--active': !config.tone }"
          :color="!config.tone ? 'primary' : undefined"
          @click="config.tone = ''">
          <span class="kpi-tone-swatch kpi-tone-swatch--none"></span>
          None
        </v-btn>
        <v-btn
          v-for="t in kpiToneOptions"
          :key="t.value"
          small
          outlined
          class="kpi-tone-picker__opt"
          :class="{ 'kpi-tone-picker__opt--active': config.tone === t.value }"
          :color="config.tone === t.value ? 'primary' : undefined"
          @click="config.tone = t.value">
          <span class="kpi-tone-swatch" :style="{ background: t.color }"></span>
          {{ t.label }}
        </v-btn>
      </div>
    </template>

    <!-- By schema: colour the value with a reusable range schema — a built-in
         inspection scale, or a custom set of bands built inline for this widget.
         Direction-free (the ordered bands carry the meaning). Serialised as
         sqlViewConfig.coloringSource:'schema' + schemaSelection (§4.1/§4.3). -->
    <template v-else-if="config.statusMode === statusModes.SCHEMA">
      <schema-picker
        :schema-id="config.schemaId"
        :value-unit="config.schemaValueUnit"
        unit-hint="The unit this KPI value is in. It is converted onto the schema bands."
        :select-items="kpiSchemaSelectItems"
        :library-loading="kpiSchemaLibraryLoading"
        :show-details="kpiSchemaShowDetails"
        :show-band-badge="kpiSchemaShowBandBadge"
        :unit-items="kpiSchemaUnitItems"
        :band-preview="kpiSchemaBandPreview"
        :is-custom="kpiSchemaIsCustom"
        :missing-reference="kpiSchemaMissingReference"
        @schema-input="config.schemaId = $event"
        @schema-change="$emit('schema-change', $event)"
        @unit-input="config.schemaValueUnit = $event">
      </schema-picker>
    </template>

    <span class="viz-sub-label mt-3">Unit</span>
    <v-text-field
      v-model="config.unit"
      placeholder="yr"
      maxlength="8"
      outlined
      dense
      hide-details
      class="kpi-unit-field">
    </v-text-field>

    <span class="viz-sub-label mt-3">Footnote</span>
    <v-text-field
      v-model="config.footnote"
      placeholder="Defaults to the source and calculation"
      hint="Shown at the bottom of the card. Numbers appear bold."
      persistent-hint
      outlined
      dense>
    </v-text-field>
  </div>
</template>

<script>
import SchemaPicker from './SchemaPicker.vue';

export default {
  name: 'WizardStepBehaviorKpi',

  components: {
    SchemaPicker,
  },

  props: {
    // Shared widget configuration (the parent's single source of truth, mutated in place).
    config: { type: Object, required: true },
    // Frozen status/direction vocabularies (owned by the parent so its serializers and these
    // toggles stay in lockstep).
    statusModes: { type: Object, required: true },
    kpiDirections: { type: Object, required: true },
    // Icon + tone choices and the inline threshold guidance/validation.
    kpiIconOptions: { type: Array, default: () => [] },
    kpiToneOptions: { type: Array, default: () => [] },
    kpiHasThreshold: { type: Boolean, default: false },
    kpiCautionHint: { type: String, default: '' },
    kpiCriticalHint: { type: String, default: '' },
    kpiThresholdError: { type: String, default: null },
    // Schema picker inputs (built by the parent, forwarded verbatim to SchemaPicker).
    kpiSchemaSelectItems: { type: Array, default: () => [] },
    kpiSchemaLibraryLoading: { type: Boolean, default: false },
    kpiSchemaShowDetails: { type: Boolean, default: false },
    kpiSchemaShowBandBadge: { type: Boolean, default: false },
    kpiSchemaUnitItems: { type: Array, default: () => [] },
    kpiSchemaBandPreview: { type: Array, default: null },
    kpiSchemaIsCustom: { type: Boolean, default: false },
    kpiSchemaMissingReference: { type: Boolean, default: false },
  },
};
</script>

<style lang="scss" scoped>
// Shared form-caption classes (duplicated from WizardStepBehavior so this child is self-contained;
// they also live there for the gauge/chart/datagrid groups).
.viz-sub-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  opacity: 0.75;
}

.viz-sub-hint {
  display: block;
  margin-top: 6px;
  font-size: 11.5px;
  opacity: 0.6;
}

// Number/KPI icon picker: a compact wrapping row of icon options + a None default.
.kpi-icon-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.kpi-icon-picker__opt--active {
  background: rgba(3, 98, 76, 0.08) !important;
}

.kpi-icon-picker__none {
  min-width: 56px;
  text-transform: none;
  letter-spacing: normal;
}

// Severity-tone picker: mirrors the icon picker layout; each option carries a swatch in the
// actual value colour so the choice previews itself.
.kpi-tone-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.kpi-tone-picker__opt {
  text-transform: none;
  letter-spacing: normal;
}

.kpi-tone-picker__opt--active {
  background: rgba(3, 98, 76, 0.08) !important;
}

.kpi-tone-swatch {
  display: inline-block;
  width: 11px;
  height: 11px;
  border-radius: 3px;
  margin-right: 6px;
  flex: 0 0 auto;
}

.kpi-tone-swatch--none {
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.3);
}

.kpi-unit-field {
  max-width: 120px;
}

// KPI threshold number fields (Caution from / Critical from): narrow, with room for a
// negative decimal boundary (e.g. remaining life -0.6).
.kpi-threshold-field {
  max-width: 160px;
}

// Inline threshold validation message. Colour comes from Vuetify `warning--text`; this class
// only owns layout so the icon + text align.
.viz-threshold-error {
  display: flex;
  align-items: center;
  margin-bottom: 0;
  font-size: 12px;
  line-height: 1.4;
}

.theme--dark {
  .kpi-tone-swatch--none {
    border-color: rgba(255, 255, 255, 0.3);
  }
}
</style>
